import { PillarCode } from "../../session/orchestration/tool-protocol/follow-up-refs.js";
import type { AttachItemBlock } from "../grammar.js";
import {
  AUTHORED_ATTACH_FIELD_KEYS,
  AUTHORED_ATTACH_SHAPE_BY_PILLAR,
  type AuthoredAttachFieldKey,
  type AuthoredAttachPillar,
} from "./authored-fields.generated.js";

/** Every value written by the twenty-two authored formats, grouped without
 * losing repeated field lines or their order. Values remain strings because
 * that is the format those backend projectors actually persist. */
export type AuthoredAttachValues<P extends AuthoredAttachPillar> = Readonly<
  Partial<Record<AuthoredAttachFieldKey<P>, readonly string[]>>
>;

/** One exact authored result arm. `pillar` and `shape` are both discriminants;
 * `values` only admits the field enum belonging to that pillar. */
export type AuthoredAttachResult<
  P extends AuthoredAttachPillar = AuthoredAttachPillar,
> = P extends AuthoredAttachPillar
  ? {
      readonly pillar: P;
      readonly shape: (typeof AUTHORED_ATTACH_SHAPE_BY_PILLAR)[P];
      /** Display projection only. No data reader may recover facts from it. */
      readonly headline: string;
      readonly values: AuthoredAttachValues<P>;
    }
  : never;

const AUTHORED_PILLARS = new Set<PillarCode>(
  Object.keys(AUTHORED_ATTACH_FIELD_KEYS) as AuthoredAttachPillar[],
);

export function isAuthoredAttachPillar(
  pillar: PillarCode,
): pillar is AuthoredAttachPillar {
  return AUTHORED_PILLARS.has(pillar);
}

/**
 * Read one authored item against its backend writer's exact field enum.
 *
 * An unknown key refuses the item. The block reader promotes that refusal to
 * the whole-block generic fallback, so a newer writer can never be rendered as
 * a misleading partly-typed record by an older frontend.
 */
export function readAuthoredAttachItem<P extends AuthoredAttachPillar>(input: {
  pillar: P;
  item: AttachItemBlock;
}): AuthoredAttachResult<P> | null {
  const allowedKeys = new Set<string>(AUTHORED_ATTACH_FIELD_KEYS[input.pillar]);
  const grouped: Record<string, string[]> = {};

  for (const field of input.item.fields) {
    if (!allowedKeys.has(field.key)) return null;
    (grouped[field.key] ??= []).push(field.value);
  }

  return {
    pillar: input.pillar,
    shape: AUTHORED_ATTACH_SHAPE_BY_PILLAR[input.pillar],
    headline: input.item.headline,
    values: grouped,
  } as unknown as AuthoredAttachResult<P>;
}
